import {
  DirectDebitElement,
  type DirectDebitElementError,
  type DirectDebitElementOnSuccessResult,
} from '@flywire/internal-react-elements';
import { createRecurringReceivable } from 'actions';
import { useCreateCheckoutSession } from 'components/CardTokenization/useCreateCheckoutSession';
import { Navigation } from 'components/Navigation/Navigation';
import { PaymentStep } from 'components/PaymentStep/PaymentStep';
import { TransactionError } from 'components/RecurringReceivables/components/TransactionError/TransactionError';
import { Spinner } from 'components/Spinner/Spinner';
import { useWizard } from 'components/Wizard/components/context';
import { FLYWIRE_JS_ENVIRONMENT } from 'constants/config';
import { STEP_RECURRING_DIRECT_DEBIT_PAYMENT } from 'constants/paymentSteps';
import { useState } from 'react';
import { useDispatch } from 'store/configureStore';
import { useTranslations } from 'utils';
import { errorNotifier } from 'utils/errorNotifier/errorNotifier';

const DirectDebit = () => {
  const i18n = useTranslations();
  const dispatch = useDispatch();
  const [failed, setFailed] = useState(false);
  const { goToPrevStep } = useWizard();

  const { createSession, createSessionError, sessionId } =
    useCreateCheckoutSession({
      sessionType: 'direct_debit_tokenization_for_installment',
    });

  const showError = createSessionError || failed;

  const onError = async (error: DirectDebitElementError) => {
    switch (error.type) {
      case 'not_found':
        errorNotifier.notify(
          new Error('DirectDebitForm error: Session expired'),
        );
        break;
      case 'internal_error':
      default:
        errorNotifier.notify(new Error(`DirectDebitForm error: ${error}`));
    }
    setFailed(true);
  };

  const onTokenizationSuccess = async (
    data: DirectDebitElementOnSuccessResult,
  ) => {
    if (!sessionId) {
      return;
    }
    try {
      await dispatch(
        createRecurringReceivable({
          apiConfirmationURL: data.confirm.url,
          sessionId,
        }),
      );
    } catch {
      setFailed(true);
    }
  };

  const handleOnRetry = async () => {
    await createSession();
    setFailed(false);
  };

  const navigation = (
    <Navigation
      hideNext
      onClickPrev={goToPrevStep}
      nextStepName={i18n.t('stepsList.track') as string}
    />
  );

  return (
    <PaymentStep
      navigation={navigation}
      stepName={STEP_RECURRING_DIRECT_DEBIT_PAYMENT}
      title={sessionId && !showError ? (i18n.t('stepsList.pay') as string) : ''}
      hasForm={false}
    >
      <div className="min-h-96 my-8">
        {showError ? (
          <TransactionError
            errorKey="direct.debit.error"
            handleOnRetry={handleOnRetry}
          />
        ) : (
          !sessionId && <Spinner className="h-96" />
        )}
        {sessionId && !showError && (
          <DirectDebitElement
            className="flex flex-wrap gap-5 justify-center min-h-96 my-8"
            sessionId={sessionId}
            onError={onError}
            onSuccess={onTokenizationSuccess}
            environment={FLYWIRE_JS_ENVIRONMENT}
          />
        )}
      </div>
    </PaymentStep>
  );
};

export { DirectDebit };
